import { useStyletron } from '@thanx/uikit/theme'
import type { LiveUpdatesTriggerMode } from 'api/liveUpdates.types'
import Badge from 'components/Badge'
import palette from 'constants/palette'
import React, { useRef } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { generateId } from 'utilities/uniqueId'

export interface TriggerBadgeProps {
  label: string
  detail?: string | null
  mode: LiveUpdatesTriggerMode
}

// Pill treatment per trigger mode: 'event' (fires on the provider webhook,
// e.g. "TRIGGERED BY OLO") reads green — Nephritis-20 on Nephritis-50 per
// Figma 1247:11633. Note this palette's `spearmint` is teal (#13C1BF), not
// green, so it is NOT the token for this pill. 'timer' (the est.-ready-time
// offset mode, e.g. "BASED ON EST. READY TIME") reads yellow/eggYolk.
// Palette tokens only — never hardcoded hex.
const MODE_COLORS: Record<
  LiveUpdatesTriggerMode,
  { background: string; text: string }
> = {
  event: { background: palette.nephritis20, text: palette.nephritis },
  timer: { background: palette.eggYolk10, text: palette.eggYolk },
}

// The small uppercase pill rendered above a Live Updates trigger card. Casing
// is a CSS text-transform (Badge's `small-caps-3` class already applies it,
// reasserted here explicitly) rather than `.toUpperCase()` on the string, so
// the underlying label/detail stay natural-case for assistive tech.
//
// When `detail` is present the pill carries a tooltip (FR-68): reachable by
// mouse hover AND keyboard focus. The wrapping span only takes a tabIndex
// when there's something to reveal on focus — a plain label with no detail
// renders as a static, non-focusable pill.
const TriggerBadge = ({ label, detail, mode }: TriggerBadgeProps) => {
  const [css] = useStyletron()
  const tooltipId = useRef(generateId()).current
  const colors = MODE_COLORS[mode]

  const pill = (
    <Badge
      className={css({
        textTransform: 'uppercase',
        backgroundColor: colors.background,
        color: colors.text,
      })}
      value={label}
    />
  )

  if (!detail) return pill

  return (
    <OverlayTrigger
      placement="top"
      trigger={['hover', 'focus']}
      overlay={
        <Tooltip id={`trigger-badge-tooltip-${tooltipId}`}>{detail}</Tooltip>
      }
    >
      <span
        tabIndex={0}
        role="note"
        aria-label={`${label} — ${detail}`}
        className={css({ display: 'inline-block' })}
      >
        {pill}
      </span>
    </OverlayTrigger>
  )
}

export default TriggerBadge
